import React, { createContext, useContext, useState, ReactNode, useCallback, useRef, useEffect } from 'react';
import { setAuthToken, refreshToken } from '../api/axiosConnector';
import { jwtDecode } from 'jwt-decode';

interface User {
  id: number | null;
  token: string | null;
  name: string | null;
  role: string | null;
  points: number | 0;
  rating: number | 0;
  address_id: number | null;
  customer_ids?: number[] | null;
  lastLoginTime?: string | null;
  can_create_self_appointments?: boolean;
  can_open_previous_slot?: boolean;
  master_admin?: boolean;
  superadmin?: boolean;
  lastVisitedPath?: string | null;
}

interface UserContextType {
  user: User;
  login: (id: number, token: string, name: string, role: string, points: number, rating: number, address_id: number, customer_ids?: number[], can_create_self_appointments?: boolean, can_open_previous_slot?: boolean, master_admin?: boolean, superadmin?: boolean) => void;
  logout: () => void;
  isAuthenticated: boolean;
  updateUserRoleInContext: (role: string) => void;
  updateToken: (token: string) => void;
  updateUserPoints: (points: number) => void;
  updateUserPermissions: (can_create_self_appointments: boolean) => void;
  updateLastVisitedPath: (path: string) => void;
  getRedirectPath: () => string;
  clearRedirectPath: () => void;
  updateCustomerIds: (customer_ids: number[]) => void;
  refreshUserData: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

let externalUpdateToken: ((token: string) => void) | null = null;
let externalLogout: (() => void) | null = null;
let refreshTimeout: ReturnType<typeof setTimeout> | null = null;

const scheduleTokenRefresh = (token: string) => {
  if (refreshTimeout) clearTimeout(refreshTimeout);
  try {
    const decoded: any = jwtDecode(token);
    if (decoded.exp) {
      const expiresIn = decoded.exp * 1000 - Date.now();
      const refreshIn = Math.max(0, expiresIn - 10000); // 10s before expiry
      refreshTimeout = setTimeout(async () => {
        try {
          await refreshToken();
        } catch (error) {
          console.error('Proactive token refresh failed:', error);
          if (externalLogout) {
            externalLogout();
          }
        }
      }, refreshIn);
    }
  } catch {
    // Invalid token, do nothing
  }
};

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User>(() => {
    // Default user state - used as fallback for any errors
    const defaultUser: User = {
      id: null,
      token: null,
      name: null,
      role: null,
      points: 0,
      rating: 0,
      address_id: null,
      lastLoginTime: null,
      can_create_self_appointments: true,
      can_open_previous_slot: false,
      master_admin: false,
      superadmin: false,
      lastVisitedPath: null,
    };

    try {
      const savedUser = localStorage.getItem('user');
      if (!savedUser) {
        return defaultUser;
      }

      try {
        const parsedUser = JSON.parse(savedUser) as User;

        // Validate that parsedUser is an object and has expected structure
        if (!parsedUser || typeof parsedUser !== 'object') {
          console.error('Invalid user data in localStorage: not an object');
          localStorage.removeItem('user');
          setAuthToken(null);
          return defaultUser;
        }

        const normalizedParsedUser = {
          ...parsedUser,
          master_admin: parsedUser.master_admin ?? false,
          superadmin: parsedUser.superadmin ?? false,
        };

        if (normalizedParsedUser.token) {
          try {
            const decoded: any = jwtDecode(normalizedParsedUser.token);
            if (decoded.exp && decoded.exp > Date.now() / 1000) {
              setAuthToken(normalizedParsedUser.token);
              scheduleTokenRefresh(normalizedParsedUser.token);
              return normalizedParsedUser;
            } else {
              console.warn('Token expired, clearing user data');
              localStorage.removeItem('user');
              setAuthToken(null);
            }
          } catch (tokenError) {
            console.error('Error decoding token:', tokenError);
            localStorage.removeItem('user');
            setAuthToken(null);
          }
        } else {
          console.warn('No token found in saved user, clearing data');
          localStorage.removeItem('user');
          setAuthToken(null);
        }
      } catch (parseError) {
        console.error('Error parsing user data from localStorage:', parseError);
        localStorage.removeItem('user');
        setAuthToken(null);
      }
    } catch (storageError) {
      console.error('Error accessing localStorage:', storageError);
      // If localStorage is completely inaccessible, still return default user
    }

    return defaultUser;
  });

  // Use ref to always access current user without causing re-renders
  const userRef = useRef(user);
  userRef.current = user;

  const login = (id: number, token: string, name: string, role: string, points: number, rating: number, address_id: number, customer_ids?: number[], can_create_self_appointments?: boolean, can_open_previous_slot?: boolean, master_admin?: boolean, superadmin?: boolean) => {
    const currentTime = new Date().toISOString();
    const newUser = {
      id,
      token,
      name,
      role,
      points,
      rating,
      address_id,
      customer_ids: customer_ids ?? [],
      lastLoginTime: currentTime,
      can_create_self_appointments: can_create_self_appointments ?? true,
      can_open_previous_slot: can_open_previous_slot ?? false,
      master_admin: master_admin ?? false,
      superadmin: superadmin ?? false,
      lastVisitedPath: user.lastVisitedPath,
    };
    setUser(newUser);
    try {
      localStorage.setItem('user', JSON.stringify(newUser));
    } catch (error) {
      console.error('Error saving user to localStorage:', error);
      // Continue anyway - user will be logged in for this session
    }
    setAuthToken(token);
    scheduleTokenRefresh(token);
  };

  const logout = () => {
    if (refreshTimeout) clearTimeout(refreshTimeout);
    const clearedUser = {
      id: null,
      token: null,
      name: null,
      role: null,
      points: 0,
      rating: 0,
      address_id: null,
      lastLoginTime: null,
      can_create_self_appointments: true,
      can_open_previous_slot: false,
      master_admin: false,
      superadmin: false,
      lastVisitedPath: null,
    };
    setUser(clearedUser);
    localStorage.removeItem('user');
    setAuthToken(null);
  };

  const updateUserRoleInContext = (role: string) => {
    const newUser = {
      ...user,
      role,
    };
    setUser(newUser);
    try {
      localStorage.setItem('user', JSON.stringify(newUser));
    } catch (error) {
      console.error('Error updating user role in localStorage:', error);
    }
  };

  const updateToken = (token: string) => {
    const newUser = { ...user, token };
    setUser(newUser);
    try {
      localStorage.setItem('user', JSON.stringify(newUser));
    } catch (error) {
      console.error('Error updating token in localStorage:', error);
    }
    setAuthToken(token);
    scheduleTokenRefresh(token);
  };

  const updateUserPoints = (points: number) => {
    const newUser = { ...user, points };
    setUser(newUser);
    try {
      localStorage.setItem('user', JSON.stringify(newUser));
    } catch (error) {
      console.error('Error updating user points in localStorage:', error);
    }
  };

  const updateUserPermissions = (can_create_self_appointments: boolean) => {
    const newUser = { ...user, can_create_self_appointments };
    setUser(newUser);
    try {
      localStorage.setItem('user', JSON.stringify(newUser));
    } catch (error) {
      console.error('Error updating user permissions in localStorage:', error);
    }
  };

  const updateCustomerIds = (customer_ids: number[]) => {
    const newUser = { ...user, customer_ids };
    setUser(newUser);
    try {
      localStorage.setItem('user', JSON.stringify(newUser));
    } catch (error) {
      console.error('Error updating customer IDs in localStorage:', error);
    }
  };

  const refreshUserData = useCallback(async () => {
    const currentUser = userRef.current;
    if (!currentUser.id || !currentUser.token) {
      console.warn('Cannot refresh user data: no user ID or token');
      return;
    }

    try {
      // Import getUserById and getUserCustomers dynamically to avoid circular dependencies
      const { getUserById, getUserCustomers } = await import('../api/apiHelperUsers');
      const userData = await getUserById(currentUser.id);

      // Fetch customer associations separately
      let customerIds: number[] | null = currentUser.customer_ids ?? null;
      try {
        console.log('[UserContext] Fetching customers for user:', currentUser.id);
        const customersResponse = await getUserCustomers(currentUser.id);
        console.log('[UserContext] Customers response:', customersResponse);
        console.log('[UserContext] Customers array:', customersResponse.customers);
        console.log('[UserContext] Customers length:', customersResponse.customers?.length);
        console.log('[UserContext] Array.isArray:', Array.isArray(customersResponse.customers));

        if (customersResponse.customers && customersResponse.customers.length > 0) {
          customerIds = customersResponse.customers.map(c => c.id);
          console.log('[UserContext] Mapped customer IDs:', customerIds);
        } else {
          customerIds = [];
          console.log('[UserContext] No customers found, setting to empty array');
        }
        console.log('User data refresh: fetched customer IDs:', customerIds);
      } catch (error) {
        console.error('Failed to fetch customer IDs during refresh, keeping current:', error);
        // Keep existing customer_ids if fetch fails
      }

      // Update user context with fresh data, keeping the token
      const updatedUser = {
        ...currentUser,
        customer_ids: customerIds,
        points: userData.points ?? currentUser.points,
        rating: userData.rating ?? currentUser.rating,
        can_create_self_appointments: userData.can_create_self_appointments ?? currentUser.can_create_self_appointments,
        can_open_previous_slot: userData.can_open_previous_slot ?? currentUser.can_open_previous_slot,
        superadmin: userData.superadmin ?? currentUser.superadmin ?? false,
        master_admin: userData.master_admin ?? currentUser.master_admin ?? false,
      };

      setUser(updatedUser);
      try {
        localStorage.setItem('user', JSON.stringify(updatedUser));
      } catch (error) {
        console.error('Error saving refreshed user data to localStorage:', error);
      }
      console.log('User data refreshed successfully', updatedUser);
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  }, []); // No dependencies - uses ref to access current user

  const updateLastVisitedPath = useCallback((path: string) => {
    // Don't save login, register, or initial paths
    if (path === '/login' || path === '/register' || path === '/' || path === '/forgot-password' || path === '/reset-password') {
      return;
    }
    setUser(prevUser => {
      // Only update if the path is different
      if (prevUser.lastVisitedPath === path) {
        return prevUser;
      }
      const newUser = { ...prevUser, lastVisitedPath: path };
      try {
        localStorage.setItem('user', JSON.stringify(newUser));
      } catch (error) {
        console.error('Error updating last visited path in localStorage:', error);
      }
      return newUser;
    });
  }, []);

  const getRedirectPath = () => {
    const redirectPath = localStorage.getItem('redirectPath');
    return redirectPath || user.lastVisitedPath || '/platform';
  };

  const clearRedirectPath = () => {
    localStorage.removeItem('redirectPath');
  };

  externalUpdateToken = updateToken;
  externalLogout = logout;

  const isAuthenticated = !!user.token;

  // Auto-refresh user data to pick up permission changes
  useEffect(() => {
    if (!isAuthenticated) return;

    // Refresh when user returns to the tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Tab became visible - refreshing user data');
        refreshUserData();
      }
    };

    // Refresh when window regains focus
    const handleFocus = () => {
      console.log('Window focused - refreshing user data');
      refreshUserData();
    };

    // Periodic refresh every 3 minutes
    const intervalId = setInterval(() => {
      console.log('Periodic refresh - refreshing user data');
      refreshUserData();
    }, 3 * 60 * 1000); // 3 minutes

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // Cleanup
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isAuthenticated, refreshUserData]);

  return (
    <UserContext.Provider value={{ user, login, logout, isAuthenticated, updateUserRoleInContext, updateToken, updateUserPoints, updateUserPermissions, updateLastVisitedPath, getRedirectPath, clearRedirectPath, updateCustomerIds, refreshUserData }}>
      {children}
    </UserContext.Provider>
  );
};

export const setUserTokenFromOutside = (token: string) => {
  if (externalUpdateToken) externalUpdateToken(token);
};

export const logoutFromOutside = () => {
  if (externalLogout) externalLogout();
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}; 